/**
 * useAgentTextSources Hook
 * Manages TextSource source attachments for agents.
 */

import { useCallback, useState } from 'react';
import adminApi, { type TextSource } from '@/services/admin.api';

interface UseAgentTextSourcesResult {
  texts: TextSource[];
  loading: boolean;
  error: string;
  loadTexts: (agentId: string) => Promise<void>;
  detachText: (agentId: string, textId: string) => Promise<void>;
}

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.error?.message) return error.response.data.error.message;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message) return error.message;
  return 'An unexpected error occurred';
};

export const useAgentTextSources = (): UseAgentTextSourcesResult => {
  const [texts, setTexts] = useState<TextSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadTexts = useCallback(async (agentId: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getAgentTextSources(agentId);
      setTexts(data);
    } catch (err: any) {
      setError(extractErrorMessage(err));
      setTexts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const detachText = useCallback(async (agentId: string, textId: string) => {
    try {
      setError('');
      await adminApi.detachTextSourceFromAgent(agentId, textId);
      setTexts((prev) => prev.filter((t) => t._id !== textId));
    } catch (err: any) {
      throw new Error(extractErrorMessage(err));
    }
  }, []);

  return {
    texts,
    loading,
    error,
    loadTexts,
    detachText,
  };
};
